import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboarding } from './OnboardingContext';
import { ChevronRight, Mail, Phone, Check, Sparkles, Clock, BookOpen, Target, Plus, X, UserX, Crown } from 'lucide-react';

// ---------------------------------------------------------------- data maps
const CLASS_LEVELS = [
    { id: 'class-6-10', label: 'Class 6–10' },
    { id: 'class-11-12', label: 'Class 11–12 (Junior College)' },
    { id: 'college-ug', label: 'College / Undergraduate' },
    { id: 'grad-prof', label: 'Graduate / Professional' },
    { id: 'general', label: 'General Learner' },
];

const EXAMS_BY_LEVEL = {
    'class-6-10': ['CBSE', 'ICSE', 'State Boards', 'NTSE / Olympiads'],
    'class-11-12': ['CBSE / State Boards', 'JEE Main / Advanced', 'NEET-UG', 'BITSAT', 'CUET-UG', 'IPMAT', 'CA Foundation'],
    'college-ug': ['GATE', 'CAT / XAT', 'GRE / GMAT', 'CUET-PG', 'CA Inter / Final'],
    'grad-prof': ['UPSC CSE', 'SSC CGL', 'Banking (IBPS / SBI PO)', 'State PSC'],
    'general': ['Self-Study', 'Skill Building', 'Concept Clarity'],
};

const SCHOOL_SUBJECTS = ['Mathematics', 'Science', 'Social Science', 'English', 'Hindi'];
const GENERIC_SUBJECTS = ['Mathematics', 'Science', 'English', 'Computer Science', 'Social Science'];

const SUBJECTS_BY_EXAM = {
    'CBSE': SCHOOL_SUBJECTS,
    'ICSE': SCHOOL_SUBJECTS,
    'State Boards': SCHOOL_SUBJECTS,
    'NTSE / Olympiads': [...SCHOOL_SUBJECTS, 'Mental Ability'],
    'CBSE / State Boards': ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'English', 'Computer Science', 'Economics', 'Accountancy'],
    'JEE Main / Advanced': ['Physics', 'Chemistry', 'Mathematics'],
    'NEET-UG': ['Physics', 'Chemistry', 'Botany', 'Zoology'],
    'BITSAT': ['Physics', 'Chemistry', 'Mathematics', 'English Proficiency', 'Logical Reasoning'],
    'CUET-UG': ['English', 'General Test', 'Domain Subjects'],
    'IPMAT': ['Quantitative Ability', 'Verbal Ability', 'Logical Reasoning'],
    'CA Foundation': ['Accounting', 'Business Laws', 'Quantitative Aptitude', 'Business Economics'],
    'GATE': ['Engineering Mathematics', 'General Aptitude', 'Core Engineering'],
    'CAT / XAT': ['VARC', 'DILR', 'Quantitative Ability'],
    'GRE / GMAT': ['Quantitative Reasoning', 'Verbal Reasoning', 'Analytical Writing'],
    'CUET-PG': ['Domain Subject', 'General Aptitude'],
    'CA Inter / Final': ['Accounting', 'Taxation', 'Auditing', 'Corporate Laws', 'Cost & Management Accounting'],
    'UPSC CSE': ['History', 'Geography', 'Polity', 'Economy', 'Environment', 'CSAT'],
    'SSC CGL': ['General Intelligence & Reasoning', 'General Awareness', 'Quantitative Aptitude', 'English'],
    'Banking (IBPS / SBI PO)': ['Reasoning Ability', 'Quantitative Aptitude', 'English Language', 'Financial Awareness'],
    'State PSC': ['General Studies', 'State GK', 'CSAT', 'Current Affairs'],
    'Self-Study': GENERIC_SUBJECTS,
    'Skill Building': GENERIC_SUBJECTS,
    'Concept Clarity': GENERIC_SUBJECTS,
};

// National exam bundles for the "+ Prepare for a National Exam" add-on
const EXAM_BUNDLES = [
    { id: 'jee', name: 'JEE Main & Advanced', subjects: ['Physics', 'Chemistry', 'Mathematics'], price: 249 },
    { id: 'neet', name: 'NEET-UG', subjects: ['Physics', 'Chemistry', 'Botany', 'Zoology'], price: 249 },
    { id: 'bitsat', name: 'BITSAT', subjects: ['English Proficiency', 'Logical Reasoning'], price: 199 },
    { id: 'olympiad', name: 'NTSE / Olympiads', subjects: ['Mental Ability', 'Advanced Mathematics'], price: 149 },
    { id: 'cuet', name: 'CUET-UG', subjects: ['General Test', 'English'], price: 149 },
    { id: 'ipmat', name: 'IPMAT', subjects: ['Quantitative Ability', 'Verbal Ability', 'Logical Reasoning'], price: 199 },
];

const TIME_COMMITMENT = [
    { id: '<1h', label: '< 1 Hour/Day', hint: 'Just warming up' },
    { id: '1-2h', label: '1–2 Hours/Day', hint: 'Steady & consistent' },
    { id: '2-3h', label: '2–3 Hours/Day', hint: 'Serious contender' },
    { id: '4h+', label: '4+ Hours/Day', hint: 'All in' },
];

const PLAN_CARDS = [
    { id: 'base', name: 'Base Plan', price: 200, credits: 150, note: '1–2 subjects, standard tools' },
    { id: 'core', name: 'Core Scholar', price: 350, credits: 300, note: 'Multi-device sync, unlimited folders', badge: 'MOST POPULAR' },
    { id: 'pro', name: 'Pro Deal', price: 500, credits: 650, note: 'Unlocks Live Canvas Tutor + priority AI', badge: '🔥 BEST VALUE' },
];

const SUB_PRICE = 100; // ₹ per subject / month in the unified builder
const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function goToGoogleAuth(remember) {
    try { localStorage.setItem('sf.remember', remember ? '1' : '0'); } catch {}
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function OnboardingModal({ inBook = false }) {
    const { user, setPhase, completeOnboarding, authResumed } = useOnboarding();
    const alreadyAuthed = authResumed || !!user?.authed || !!user?.email;
    const [step, setStep] = useState(alreadyAuthed ? 1 : 0);
    const [examPickerOpen, setExamPickerOpen] = useState(false);
    const [bundleQuery, setBundleQuery] = useState('');
    const [data, setData] = useState({
        authMethod: 'email',
        email: user?.email || '',
        phone: '',
        keepSignedIn: true,
        is_guest: false,
        name: user?.name || '',
        // Step 1
        class_level: user?.class_level || '',
        primary_exam: '',
        // Step 2
        time_commitment: user?.time_commitment || '',
        // Step 3
        focus_areas: user?.focus_areas || [],
        national_exams: user?.additional_national_exams || [], // bundle ids
        // Step 4 — plan: {type, price_inr, credits}
        plan: null,
    });

    const FIRST_STEP = alreadyAuthed ? 1 : 0;
    const TOTAL = 6;

    // Step 3: core subjects filtered by level + exam
    const coreSubjects = useMemo(
        () => SUBJECTS_BY_EXAM[data.primary_exam] || GENERIC_SUBJECTS,
        [data.primary_exam],
    );
    // Subjects covered by added national bundles (for dedupe in the builder)
    const bundleSubjects = useMemo(() => {
        const set = new Set();
        EXAM_BUNDLES.filter((b) => data.national_exams.includes(b.id)).forEach((b) => b.subjects.forEach((s) => set.add(s)));
        return set;
    }, [data.national_exams]);
    const allSubjects = useMemo(() => {
        const merged = [...data.focus_areas];
        bundleSubjects.forEach((s) => { if (!merged.includes(s)) merged.push(s); });
        return merged;
    }, [data.focus_areas, bundleSubjects]);

    // Unified builder math: ₹100/subject, waived when covered by a bundle
    const chargeableSubjects = data.focus_areas.filter((s) => !bundleSubjects.has(s));
    const bundleTotal = EXAM_BUNDLES.filter((b) => data.national_exams.includes(b.id)).reduce((a, b) => a + b.price, 0);
    const customTotal = chargeableSubjects.length * SUB_PRICE + bundleTotal;

    function next() {
        if (step < TOTAL - 1) setStep(step + 1);
        else finish();
    }
    function back() {
        if (step > FIRST_STEP) setStep(step - 1);
    }
    function finish() {
        const levelLabel = CLASS_LEVELS.find((l) => l.id === data.class_level)?.label || data.class_level;
        const plan = data.plan || { type: 'free', price_inr: 0, credits: 20 };
        const examNames = EXAM_BUNDLES.filter((b) => data.national_exams.includes(b.id)).map((b) => b.name);
        completeOnboarding({
            ...data,
            name: data.name || 'Scholar',
            board_class: data.primary_exam || levelLabel,
            education: levelLabel,
            class_level: levelLabel,
            primary_exam_track: data.primary_exam,
            additional_national_exams: examNames,
            primary_objective: `Master ${allSubjects.slice(0, 3).join(', ')} for ${data.primary_exam}`,
            goal: `${data.primary_exam} — ${allSubjects.join(', ')}`,
            plan_tier: plan.type,
            subscription_tier: plan.type,
            plan_price_inr: plan.price_inr,
            plan_credits: plan.credits,
        });
        setPhase('complete');
    }

    function toggleSubject(s) {
        setData((d) => {
            const has = d.focus_areas.includes(s);
            return { ...d, focus_areas: has ? d.focus_areas.filter((x) => x !== s) : [...d.focus_areas, s] };
        });
    }
    function toggleBundle(id) {
        setData((d) => {
            const has = d.national_exams.includes(id);
            return { ...d, national_exams: has ? d.national_exams.filter((x) => x !== id) : [...d.national_exams, id] };
        });
    }

    const stepValid =
        (step === 0 && (data.is_guest || data.email.trim().length > 3 || data.phone.trim().length >= 6)) ||
        (step === 1 && !!data.class_level && !!data.primary_exam) ||
        (step === 2 && !!data.time_commitment) ||
        (step === 3 && data.focus_areas.length > 0) ||
        step === 4 ||
        step === 5;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative w-full flex items-center justify-center px-4"
            data-testid="onboarding-modal"
        >
            <motion.div
                initial={{ y: 30, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.7, ease: [0.83, 0, 0.17, 1] }}
                className={`relative w-full overflow-hidden mx-auto ${inBook ? 'max-w-[380px]' : 'max-w-md'}`}
                style={{
                    maxHeight: inBook ? 'min(90vh, 560px)' : 'min(92vh, 720px)',
                    background: 'linear-gradient(160deg, rgba(255,255,255,0.55) 0%, rgba(245,240,255,0.35) 100%)',
                    backdropFilter: 'blur(16px) saturate(140%)',
                    WebkitBackdropFilter: 'blur(16px) saturate(140%)',
                    borderRadius: '18px',
                    border: '1px solid rgba(255,255,255,0.6)',
                    boxShadow: '0 30px 70px -20px rgba(11,15,7,0.55), 0 10px 30px -10px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 0 40px rgba(255,255,255,0.25)',
                }}
            >
                {/* specular top sheen */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-24"
                    style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)' }} />
                {/* Grain */}
                <div className="pointer-events-none absolute inset-0"
                    style={{
                        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 300 300' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.4' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.24 0 0 0 0 0.27 0 0 0 0 0.12 0 0 0 0 0.28 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                        opacity: 0.14, mixBlendMode: 'multiply',
                    }} />
                <div className={`relative ${inBook ? 'p-4 sm:p-6' : 'p-7 sm:p-9'} overflow-y-auto no-scrollbar`} style={{ maxHeight: inBook ? 'min(90vh, 560px)' : 'min(92vh, 720px)' }}>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                        <div>
                            <div className="mono text-[10px] text-[color:var(--moss-deep)]" data-testid="step-indicator">
                                STEP {step + 1} / {TOTAL}
                            </div>
                            <div className="mono text-[9px] text-[color:var(--moss-deep)]/70">Metis · personal folio setup</div>
                        </div>
                        <div className="text-right">
                            <div className="mono text-[10px] text-[color:var(--moss-deep)]">studyfolio</div>
                            <div className="mono text-[9px] text-[color:var(--moss-deep)]/70">Est. 2026</div>
                        </div>
                    </div>

                    {/* Step dots */}
                    <div className="flex items-center gap-1.5 mb-6" data-testid="onboarding-progress">
                        {Array.from({ length: TOTAL }).map((_, i) => (
                            <div key={i} className="h-[3px] flex-1 rounded-full overflow-hidden bg-[color:var(--moss-deep)]/15">
                                <motion.div
                                    initial={false}
                                    animate={{ scaleX: i <= step ? 1 : 0 }}
                                    style={{ transformOrigin: 'left', background: 'var(--lavender-deep)' }}
                                    transition={{ duration: 0.55, ease: [0.83, 0, 0.17, 1] }}
                                    className="h-full"
                                />
                            </div>
                        ))}
                    </div>

                    <AnimatePresence mode="wait">
                        {/* ---------- STEP 0: AUTH ---------- */}
                        {step === 0 && (
                            <StepShell key="s0" title="Come in." kicker="First — introduce yourself.">
                                <button type="button" onClick={() => goToGoogleAuth(data.keepSignedIn)} data-testid="auth-google"
                                    className="w-full mb-3 flex items-center justify-center gap-3 py-2.5 rounded-sm border border-[color:var(--moss-deep)]/40 bg-white/60 hover:bg-white transition-colors text-[color:var(--ink)] text-sm">
                                    <GoogleGlyph />
                                    <span>Sign in with Google</span>
                                </button>
                                <Divider label="or" />
                                <TabToggle value={data.authMethod} onChange={(v) => setData((d) => ({ ...d, authMethod: v }))}
                                    options={[
                                        { id: 'email', label: 'Email', icon: <Mail size={12} /> },
                                        { id: 'phone', label: 'Phone', icon: <Phone size={12} /> },
                                    ]} />
                                {data.authMethod === 'email' ? (
                                    <Input label="Email" type="email" placeholder="you@somewhere.io" value={data.email}
                                        onChange={(v) => setData((d) => ({ ...d, email: v }))} testid="auth-email" />
                                ) : (
                                    <Input label="Phone" type="tel" placeholder="+91 98••• •••••" value={data.phone}
                                        onChange={(v) => setData((d) => ({ ...d, phone: v }))} testid="auth-phone" />
                                )}
                                <label className="flex items-center gap-2 mt-4 text-[13px] text-[color:var(--ink)] cursor-pointer select-none" data-testid="keep-signed-in-label">
                                    <span className={`h-4 w-4 rounded-sm border border-[color:var(--moss-deep)] flex items-center justify-center transition-all ${data.keepSignedIn ? 'bg-[color:var(--lavender-deep)]' : 'bg-transparent'}`}>
                                        {data.keepSignedIn && <Check size={10} className="text-white" />}
                                    </span>
                                    <input type="checkbox" checked={data.keepSignedIn}
                                        onChange={(e) => setData((d) => ({ ...d, keepSignedIn: e.target.checked }))}
                                        className="hidden" data-testid="keep-signed-in" />
                                    Keep me signed in
                                </label>
                                <button
                                    type="button"
                                    onClick={() => { setData((d) => ({ ...d, is_guest: true })); setStep(1); }}
                                    data-testid="auth-guest-skip"
                                    className="mt-4 w-full flex items-center justify-center gap-2 mono text-[10px] text-[color:var(--moss-deep)] hover:text-[color:var(--lavender-deep)] transition-colors py-1"
                                >
                                    <UserX size={12} /> Skip sign-in — continue as guest
                                </button>
                            </StepShell>
                        )}

                        {/* ---------- STEP 1: GOAL & LEVEL (dual dropdowns) ---------- */}
                        {step === 1 && (
                            <StepShell key="s1" title="Where are you headed?" kicker="Your class and your target — Metis calibrates both." icon={<Target size={14} />}>
                                <label className="block">
                                    <span className="mono text-[10px] text-[color:var(--moss-deep)]">CLASS / GRADE LEVEL</span>
                                    <select
                                        value={data.class_level}
                                        onChange={(e) => setData((d) => ({ ...d, class_level: e.target.value, primary_exam: '' }))}
                                        data-testid="class-level-select"
                                        className="w-full mt-1 bg-white/50 border border-[color:var(--moss-deep)]/40 rounded-sm px-3 py-2.5 text-[13px] text-[color:var(--ink)] outline-none focus:border-[color:var(--lavender-deep)]"
                                    >
                                        <option value="">Select your level…</option>
                                        {CLASS_LEVELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                                    </select>
                                </label>
                                <label className="block mt-4">
                                    <span className="mono text-[10px] text-[color:var(--moss-deep)]">PRIMARY TARGET / EXAM</span>
                                    <select
                                        value={data.primary_exam}
                                        onChange={(e) => setData((d) => ({ ...d, primary_exam: e.target.value }))}
                                        disabled={!data.class_level}
                                        data-testid="primary-exam-select"
                                        className="w-full mt-1 bg-white/50 border border-[color:var(--moss-deep)]/40 rounded-sm px-3 py-2.5 text-[13px] text-[color:var(--ink)] outline-none focus:border-[color:var(--lavender-deep)] disabled:opacity-40"
                                    >
                                        <option value="">{data.class_level ? 'Select your target…' : 'Pick a level first'}</option>
                                        {(EXAMS_BY_LEVEL[data.class_level] || []).map((x) => <option key={x} value={x}>{x}</option>)}
                                    </select>
                                </label>
                            </StepShell>
                        )}

                        {/* ---------- STEP 2: TIME ---------- */}
                        {step === 2 && (
                            <StepShell key="s2" title="How much time can you give?" kicker="Be honest — Metis calibrates to reality." icon={<Clock size={14} />}>
                                <div className="space-y-2 mt-2" data-testid="time-commitment-options">
                                    {TIME_COMMITMENT.map((t) => {
                                        const active = data.time_commitment === t.id;
                                        return (
                                            <button key={t.id} data-testid={`time-${t.id}`}
                                                onClick={() => setData((d) => ({ ...d, time_commitment: t.id }))}
                                                className={`w-full text-left px-4 py-3 rounded-sm border transition-all flex items-center justify-between ${
                                                    active ? 'bg-[color:var(--lavender)]/60 border-[color:var(--lavender-deep)]' : 'bg-white/40 border-[color:var(--moss-deep)]/30 hover:border-[color:var(--moss-deep)]'}`}>
                                                <div>
                                                    <div className="text-[14px] text-[color:var(--ink)]">{t.label}</div>
                                                    <div className="mono text-[10px] text-[color:var(--moss-deep)]/70 mt-0.5">{t.hint}</div>
                                                </div>
                                                {active && <Check size={14} className="text-[color:var(--lavender-deep)]" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </StepShell>
                        )}

                        {/* ---------- STEP 3: SUBJECTS + NATIONAL EXAM ADD-ON ---------- */}
                        {step === 3 && (
                            <StepShell key="s3" title="Which subjects need Metis most?" kicker={`Filtered for ${data.primary_exam} — nothing unrelated.`} icon={<BookOpen size={14} />}>
                                <div className="flex flex-wrap gap-2 mt-3" data-testid="focus-area-options">
                                    {coreSubjects.map((s) => {
                                        const active = data.focus_areas.includes(s);
                                        return (
                                            <button key={s} data-testid={`subject-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                                onClick={() => toggleSubject(s)}
                                                className={`px-4 py-2 rounded-full mono text-[11px] border transition-all flex items-center gap-1.5 ${
                                                    active ? 'bg-[color:var(--lavender)] border-[color:var(--lavender-deep)] text-[color:var(--moss-dark)]' : 'bg-transparent border-[color:var(--moss-deep)]/40 text-[color:var(--moss-deep)] hover:border-[color:var(--moss-deep)]'}`}>
                                                {active && <Check size={10} />}
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* National exam add-on */}
                                <button
                                    onClick={() => setExamPickerOpen((v) => !v)}
                                    data-testid="add-national-exam"
                                    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm border border-dashed border-[color:var(--lavender-deep)] text-[color:var(--lavender-deep)] mono text-[11px] hover:bg-[color:var(--lavender)]/20 transition-colors"
                                >
                                    <Plus size={13} /> Prepare for a National Exam
                                </button>

                                {/* Added exam chips */}
                                {data.national_exams.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-3" data-testid="national-exam-chips">
                                        {EXAM_BUNDLES.filter((b) => data.national_exams.includes(b.id)).map((b) => (
                                            <span key={b.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--moss-deep)] text-[color:var(--parchment)] mono text-[10px]" data-testid={`chip-${b.id}`}>
                                                {b.name}
                                                <button onClick={() => toggleBundle(b.id)} aria-label={`Remove ${b.name}`} data-testid={`chip-remove-${b.id}`}>
                                                    <X size={11} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Bundle picker drawer */}
                                <AnimatePresence>
                                    {examPickerOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                            className="mt-3 border border-[color:var(--moss-deep)]/30 rounded-sm bg-white/50 overflow-hidden"
                                            data-testid="exam-picker"
                                        >
                                            <input
                                                value={bundleQuery}
                                                onChange={(e) => setBundleQuery(e.target.value)}
                                                placeholder="Search exams — JEE, NEET, Olympiads…"
                                                data-testid="exam-picker-search"
                                                className="w-full bg-transparent border-b border-[color:var(--moss-deep)]/20 px-3 py-2 text-[12px] text-[color:var(--ink)] placeholder:text-[color:var(--moss-deep)]/50 outline-none"
                                            />
                                            <div className="max-h-40 overflow-y-auto no-scrollbar">
                                                {EXAM_BUNDLES.filter((b) => b.name.toLowerCase().includes(bundleQuery.toLowerCase())).map((b) => {
                                                    const added = data.national_exams.includes(b.id);
                                                    return (
                                                        <button key={b.id} onClick={() => toggleBundle(b.id)} data-testid={`bundle-${b.id}`}
                                                            className="w-full text-left px-3 py-2.5 hover:bg-[color:var(--lavender)]/20 transition-colors flex items-center justify-between gap-2">
                                                            <div>
                                                                <div className="text-[12.5px] text-[color:var(--ink)]">{b.name}</div>
                                                                <div className="mono text-[9px] text-[color:var(--moss-deep)]/70">adds {b.subjects.join(', ')}</div>
                                                            </div>
                                                            <span className={`mono text-[9px] px-2 py-1 rounded-sm border ${added ? 'bg-[color:var(--lavender)] border-[color:var(--lavender-deep)]' : 'border-[color:var(--moss-deep)]/40 text-[color:var(--moss-deep)]'}`}>
                                                                {added ? 'ADDED ✓' : '+ ADD'}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {allSubjects.length > 0 && (
                                    <div className="mono text-[10px] text-[color:var(--moss-deep)]/70 mt-3" data-testid="focus-area-count">
                                        {allSubjects.length} active subject{allSubjects.length > 1 ? 's' : ''} (bundle subjects merged)
                                    </div>
                                )}
                            </StepShell>
                        )}

                        {/* ---------- STEP 4: PLAN SELECTION + UNIFIED BUILDER ---------- */}
                        {step === 4 && (
                            <StepShell key="s4" title="Pick your power-up." kicker="Plans, or build your own à la carte pack." icon={<Crown size={14} />}>
                                <div className="space-y-2" data-testid="plan-cards">
                                    {PLAN_CARDS.map((p) => {
                                        const active = data.plan?.type === p.id;
                                        return (
                                            <button key={p.id} data-testid={`plan-${p.id}`}
                                                onClick={() => setData((d) => ({ ...d, plan: { type: p.id, price_inr: p.price, credits: p.credits } }))}
                                                className={`relative w-full text-left px-4 py-3 rounded-sm border transition-all ${
                                                    active ? 'bg-[color:var(--lavender)]/60 border-[color:var(--lavender-deep)]' : 'bg-white/40 border-[color:var(--moss-deep)]/30 hover:border-[color:var(--moss-deep)]'}`}>
                                                {p.badge && (
                                                    <span className="absolute -top-2 left-3 mono text-[8px] tracking-[0.12em] bg-[color:var(--lavender-deep)] text-white px-1.5 py-0.5 rounded-sm">{p.badge}</span>
                                                )}
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="text-[14px] text-[color:var(--ink)] font-medium">{p.name}</div>
                                                        <div className="mono text-[9px] text-[color:var(--moss-deep)]/70 mt-0.5">{p.note}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="display text-[color:var(--ink)] text-xl tabular-nums">{fmt(p.price)}<span className="mono text-[9px] text-[color:var(--moss-deep)]">/mo</span></div>
                                                        <div className="mono text-[9px] text-[#7A5FCB]">{p.credits} credits</div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Unified à la carte builder */}
                                <button
                                    data-testid="plan-custom"
                                    onClick={() => setData((d) => ({ ...d, plan: { type: 'custom', price_inr: customTotal, credits: 20 + data.focus_areas.length * 50 } }))}
                                    className={`mt-3 w-full text-left px-4 py-3 rounded-sm border-2 border-dashed transition-all ${
                                        data.plan?.type === 'custom' ? 'border-[color:var(--lavender-deep)] bg-[color:var(--lavender)]/30' : 'border-[color:var(--moss-deep)]/40 bg-white/30 hover:border-[color:var(--lavender-deep)]'}`}>
                                    <div className="mono text-[9px] tracking-[0.18em] text-[color:var(--moss-deep)]">CUSTOM PACK BUILDER</div>
                                    <div className="text-[12px] text-[color:var(--ink)] mt-1">
                                        {data.focus_areas.length} subject{data.focus_areas.length !== 1 ? 's' : ''} × {fmt(SUB_PRICE)}
                                        {data.national_exams.length > 0 && ` + ${data.national_exams.length} exam bundle${data.national_exams.length > 1 ? 's' : ''}`}
                                    </div>
                                    {/* Smart dedupe line */}
                                    {data.focus_areas.some((s) => bundleSubjects.has(s)) && (
                                        <div className="mono text-[9px] text-emerald-700 mt-1" data-testid="dedupe-note">
                                            ✓ {data.focus_areas.filter((s) => bundleSubjects.has(s)).join(', ')} already covered by bundle — fee waived
                                        </div>
                                    )}
                                    <div className="display text-[color:var(--ink)] text-xl tabular-nums mt-1" data-testid="custom-pack-total">
                                        {fmt(customTotal)}<span className="mono text-[9px] text-[color:var(--moss-deep)]">/mo</span>
                                    </div>
                                </button>

                                {/* Free tier skip */}
                                <button
                                    onClick={() => { setData((d) => ({ ...d, plan: { type: 'free', price_inr: 0, credits: 20 } })); setStep(5); }}
                                    data-testid="plan-free-skip"
                                    className="mt-3 w-full mono text-[10px] text-[color:var(--moss-deep)] hover:text-[color:var(--lavender-deep)] transition-colors py-2"
                                >
                                    Continue with Free Tier (20 Credits) — no payment needed →
                                </button>
                            </StepShell>
                        )}

                        {/* ---------- STEP 5: SUMMARY ---------- */}
                        {step === 5 && (
                            <StepShell key="s5" title="Metis is ready." kicker="Confirm your folio and step inside." icon={<Sparkles size={14} />}>
                                {!user?.name && (
                                    <Input label="Your name" autoFocus placeholder="e.g. Anaya Iyer" value={data.name}
                                        onChange={(v) => setData((d) => ({ ...d, name: v }))} testid="onboarding-name" />
                                )}
                                <div className="mt-3 border border-[color:var(--moss-deep)]/25 bg-white/40 rounded-sm p-4 space-y-2" data-testid="onboarding-summary">
                                    <SummaryRow k="Mode" v={data.is_guest ? 'Guest (local only)' : (data.email || data.phone || 'Google account')} />
                                    <SummaryRow k="Class" v={CLASS_LEVELS.find((l) => l.id === data.class_level)?.label || '—'} />
                                    <SummaryRow k="Target" v={data.primary_exam || '—'} />
                                    <SummaryRow k="Subjects" v={allSubjects.join(' · ') || '—'} />
                                    <SummaryRow k="Nat. Exams" v={EXAM_BUNDLES.filter((b) => data.national_exams.includes(b.id)).map((b) => b.name).join(' · ') || '—'} />
                                    <SummaryRow k="Time" v={TIME_COMMITMENT.find((t) => t.id === data.time_commitment)?.label || '—'} />
                                    <SummaryRow k="Plan" v={
                                        data.plan?.type === 'custom'
                                            ? `Custom Pack — ${fmt(data.plan.price_inr)}/mo`
                                            : data.plan
                                                ? `${PLAN_CARDS.find((p) => p.id === data.plan.type)?.name || 'Free Tier'} — ${fmt(data.plan.price_inr)}/mo`
                                                : 'Free Tier — 20 credits'
                                    } />
                                </div>
                                <div className="mono text-[10px] text-[color:var(--moss-deep)]/70 mt-3 italic">
                                    “Let's begin — one page, one question at a time.”
                                </div>
                            </StepShell>
                        )}
                    </AnimatePresence>

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-6">
                        <button onClick={back} disabled={step === FIRST_STEP} data-testid="onboarding-back"
                            className="mono text-[11px] text-[color:var(--moss-deep)] disabled:opacity-30 hover:text-[color:var(--lavender-deep)] transition">
                            ← Back
                        </button>
                        <button onClick={next} disabled={!stepValid} data-testid="onboarding-next"
                            className="btn-lavender rounded-sm px-5 py-2.5 text-xs mono disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                            {step === TOTAL - 1 ? 'Open my folio' : 'Next'}
                            <ChevronRight size={13} />
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}

function StepShell({ title, kicker, children, icon }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45, ease: [0.83, 0, 0.17, 1] }}
        >
            <div className="mono text-[10px] text-[color:var(--moss-deep)]/70 mb-1 flex items-center gap-1.5">
                {icon}
                {kicker}
            </div>
            <h3 className="display text-[color:var(--ink)] mb-4" style={{ fontSize: 'clamp(24px, 3vw, 32px)', lineHeight: 1.05 }}>
                {title}
            </h3>
            {children}
        </motion.div>
    );
}

function SummaryRow({ k, v }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="mono text-[10px] uppercase text-[color:var(--moss-deep)]/70 pt-0.5">{k}</div>
            <div className="text-[13px] text-[color:var(--ink)] text-right flex-1">{v}</div>
        </div>
    );
}

function Input({ label, value, onChange, placeholder, type = 'text', autoFocus, testid }) {
    return (
        <label className="block mt-1">
            <span className="mono text-[10px] text-[color:var(--moss-deep)]">{label}</span>
            <input
                data-testid={testid}
                autoFocus={autoFocus}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full mt-1 bg-transparent border-b border-[color:var(--moss-deep)] outline-none py-2 text-[color:var(--ink)] placeholder:text-[color:var(--moss-deep)]/50 focus:border-[color:var(--lavender-deep)] transition-colors"
            />
        </label>
    );
}

function TabToggle({ value, onChange, options }) {
    return (
        <div className="inline-flex items-center rounded-full border border-[color:var(--moss-deep)]/40 p-1 mb-2 bg-white/40">
            {options.map((o) => (
                <button
                    key={o.id}
                    onClick={() => onChange(o.id)}
                    className={`px-3 py-1 rounded-full text-[11px] mono flex items-center gap-1.5 transition-colors ${
                        value === o.id ? 'bg-[color:var(--lavender)] text-[color:var(--moss-dark)]' : 'text-[color:var(--moss-deep)]'
                    }`}
                >
                    {o.icon} {o.label}
                </button>
            ))}
        </div>
    );
}

function Divider({ label }) {
    return (
        <div className="flex items-center gap-3 my-3 text-[color:var(--moss-deep)]/50">
            <div className="flex-1 h-px bg-[color:var(--moss-deep)]/25" />
            <span className="mono text-[9px]">{label}</span>
            <div className="flex-1 h-px bg-[color:var(--moss-deep)]/25" />
        </div>
    );
}

function GoogleGlyph() {
    return (
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
    );
}
